import { useState } from 'react';
import { useMatchCommunication, useUnmatchedCommunications, CHANNELS, OUTCOMES, labelOf, type Channel, type Communication } from '../api/communications.ts';
import { CommunicationForm, LogButtons } from '../components/communications/CommunicationForms.tsx';
import { EntryContent, when } from '../components/communications/CommunicationsFeed.tsx';
import { field, label, primary, secondary } from '../components/contacts/Sheet.tsx';

/**
 * `/communications/unmatched` — communications not yet tied to a client (138): the receptionist's calls, and later
 * the call log's and the mailbox's. Seen by tiers 1-2, the receptionist, and whoever logged each one. Logging one
 * here needs no client; matching it needs a client the reader can see, so the receptionist logs and somebody who
 * can see the client matches, by the client's reference.
 */
export function UnmatchedCommunications() {
  const q = useUnmatchedCommunications();
  const [adding, setAdding] = useState<Channel | null>(null);
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-4xl space-y-3 px-4 py-6">
        <h1 className="title-bar">Communications not yet matched to a client</h1>
        <p className="text-[0.9375rem] text-neutral-700">
          Calls and messages logged before anyone knew which client they were about. Somebody who can see the client
          matches each one by the client’s reference; it then appears in that client’s Communications.
        </p>
        {q.error && <p role="alert" className="text-warning-fg">{(q.error as Error).message}</p>}
        {q.data?.may_log && !adding && (
          <>
            <p className="text-sm text-neutral-700">Log one with no client:</p>
            <LogButtons onPick={setAdding} />
          </>
        )}
        {adding && q.data && (
          <CommunicationForm key={adding} clientId={null} ctx={{ ...q.data, contacts: [] }} channel={adding} onDone={() => setAdding(null)} />
        )}
        {q.data && !q.data.sees_all_unmatched && (
          <p className="text-sm text-neutral-600">You see the ones you logged; tiers 1-2 and the receptionist see them all.</p>
        )}
        {q.data && (q.data.entries.length === 0 ? <p className="text-neutral-700">Nothing is waiting to be matched.</p> : (
          <ol className="space-y-2">{q.data.entries.map((e) => <Unmatched key={e.id} e={e} />)}</ol>
        ))}
      </div>
    </div>
  );
}

function Unmatched({ e }: { e: Communication }) {
  const [matching, setMatching] = useState(false);
  const [ref, setRef] = useState('');
  const m = useMatchCommunication(e.id);
  return (
    <li className={`rounded-lg border bg-panel p-3 ${e.is_emergency ? 'border-critical-edge' : 'border-neutral-300'}`}>
      {e.is_emergency && <p className="-mx-3 -mt-3 mb-2 rounded-t-lg bg-critical-bg px-3 py-1 text-sm font-semibold text-critical-fg">Emergency</p>}
      <p className="flex flex-wrap gap-x-2 text-sm text-neutral-700">
        <span className="font-semibold text-neutral-900">{when(e.occurred_at)}</span>
        <span>{labelOf(CHANNELS, e.channel)}{e.direction !== 'none' ? `, ${e.direction}` : ''}</span>
        {e.who && <span>with {e.who}</span>}
        {(e.contact_phone || e.contact_email) && <span>{[e.contact_phone, e.contact_email].filter(Boolean).join(' · ')}</span>}
        <span className="rounded bg-tint px-1.5 py-0.5 font-semibold text-brand-text">{e.department}</span>
        <span className="rounded bg-note-bg px-1.5 py-0.5 text-note-fg">{labelOf(OUTCOMES, e.outcome)}</span>
      </p>
      <EntryContent e={e} />
      <p className="text-sm text-neutral-600">Logged by {e.recorded_by ?? 'someone not on record'}</p>
      {!matching ? (
        <button type="button" className={`${secondary} mt-2`} onClick={() => setMatching(true)}>Match to a client</button>
      ) : (
        <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(ev) => { ev.preventDefault(); m.mutate(ref.trim()); }}>
          <label className={label}>The client’s reference
            <input value={ref} onChange={(ev) => setRef(ev.target.value)} placeholder="0001" autoComplete="off" className={field} />
          </label>
          <button type="submit" disabled={!/^X?\d{1,4}$/i.test(ref.trim()) || m.isPending} className={primary}>Match</button>
          <button type="button" className={secondary} onClick={() => setMatching(false)}>Cancel</button>
          {m.error && <p role="alert" className="w-full text-sm text-warning-fg">{(m.error as Error).message}</p>}
        </form>
      )}
    </li>
  );
}
